/* ============================================================
   NovaX Web — WebSocket Hook
   ============================================================ */

import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '../store';
import type { WSIncomingMessage, WSOutgoingMessage, ToolCall } from '../types';
import { notifyTaskComplete } from '../utils/notifications';

const HEARTBEAT_INTERVAL = 25_000;
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30_000;

/** Maps server session UUID → local chatId for session_history response mapping. */
const serverSessionIdMap = new Map<string, string>();

export function useWebSocket() {
  const token = useStore((s) => s.token);
  const setConnected = useStore((s) => s.setConnected);
  const setBots = useStore((s) => s.setBots);
  const updateMessageState = useStore((s) => s.updateMessageState);
  const addMessage = useStore((s) => s.addMessage);
  const addMessageAttachment = useStore((s) => s.addMessageAttachment);
  const markRunningMessagesDisconnected = useStore((s) => s.markRunningMessagesDisconnected);
  const addGroup = useStore((s) => s.addGroup);
  const removeGroup = useStore((s) => s.removeGroup);
  const updateGroup = useStore((s) => s.updateGroup);
  const setGroups = useStore((s) => s.setGroups);
  const createGroupSession = useStore((s) => s.createGroupSession);
  const setIncomingVoiceCall = useStore((s) => s.setIncomingVoiceCall);
  const addActivityEvent = useStore((s) => s.addActivityEvent);
  const renameSession = useStore((s) => s.renameSession);
  const deleteSession = useStore((s) => s.deleteSession);
  const mergeServerSessions = useStore((s) => s.mergeServerSessions);
  const loadServerHistory = useStore((s) => s.loadServerHistory);
  const setAsrState = useStore((s) => s.setAsrState);
  const setAsrPartialText = useStore((s) => s.setAsrPartialText);

  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || !mountedRef.current) return;
    cleanup();

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      reconnectAttempt.current = 0;
      setConnected(true);

      // Request group list
      ws.send(JSON.stringify({ type: 'list_groups' }));

      // Subscribe to grouptalk chatIds for existing group sessions
      // and resume any sessions that had running tasks
      const store = useStore.getState();
      const resumeChatIds: string[] = [];
      for (const session of store.sessions.values()) {
        if (session.groupId) {
          ws.send(JSON.stringify({ type: 'subscribe_group', groupId: session.groupId, chatId: session.id }));
        }
        // Check if any message was in running/thinking state
        const lastMsg = session.messages[session.messages.length - 1];
        if (lastMsg?.state?.status === 'running' || lastMsg?.state?.status === 'thinking') {
          resumeChatIds.push(session.id);
        }
      }
      if (resumeChatIds.length > 0) {
        ws.send(JSON.stringify({ type: 'resume', chatIds: resumeChatIds }));
      }

      // Start heartbeat
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, HEARTBEAT_INTERVAL);
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      // Binary frames are not expected from server (all ASR results come as JSON)
      if (typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data) as WSIncomingMessage;
        switch (msg.type) {
          case 'connected':
            setBots(msg.bots);
            // Request server-side sessions for each bot to restore history
            for (const bot of msg.bots) {
              ws.send(JSON.stringify({ type: 'list_sessions', botName: bot.name }));
            }
            break;

          case 'bots_updated':
            setBots(msg.bots);
            break;

          case 'state':
          case 'complete': {
            const store = useStore.getState();
            if (msg.groupId) {
              // Group chat message — find the session by groupId
              const groupSession = Array.from(store.sessions.values()).find(
                (s) => s.groupId === msg.groupId,
              );
              if (groupSession) {
                const existingMsg = groupSession.messages.find((m) => m.id === msg.messageId);
                if (!existingMsg) {
                  addMessage(groupSession.id, {
                    id: msg.messageId,
                    type: 'assistant',
                    text: '',
                    state: msg.state,
                    timestamp: Date.now(),
                    botName: msg.botName,
                  });
                } else {
                  updateMessageState(groupSession.id, msg.messageId, msg.state, msg.botName);
                }
              }
            } else {
              // Private chat — verify the session exists and isn't a group session
              // to prevent messages from leaking into wrong sessions
              const targetSession = store.sessions.get(msg.chatId);
              if (targetSession && !targetSession.groupId) {
                updateMessageState(msg.chatId, msg.messageId, msg.state, msg.botName);
              }
            }
            // Desktop notification when task completes while tab is unfocused
            if (msg.type === 'complete' && msg.state.status === 'complete') {
              notifyTaskComplete(msg.botName || 'NovaX', msg.state.responseText?.slice(0, 100));
            }
            break;
          }

          case 'error': {
            // Find the session for this chatId and update the message
            const store = useStore.getState();
            const session = Array.from(store.sessions.values()).find(
              (s) => s.id === msg.chatId,
            );
            if (session) {
              const assistantMsg = session.messages.find(
                (m) => m.id === msg.messageId,
              );
              if (assistantMsg) {
                updateMessageState(msg.chatId, msg.messageId, {
                  ...(assistantMsg.state || {
                    status: 'error',
                    userPrompt: '',
                    responseText: '',
                    toolCalls: [],
                  }),
                  status: 'error',
                  errorMessage: msg.error,
                });
              }
            }
            break;
          }

          case 'file': {
            // Show output file as a separate file message
            addMessage(msg.chatId, {
              id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              type: 'assistant',
              text: '',
              timestamp: Date.now(),
              attachments: [{
                name: msg.name,
                type: msg.mimeType,
                size: msg.size || 0,
                url: msg.url,
                path: '',
              }],
            });
            break;
          }

          case 'notice': {
            // Only route notices to a session if the server specified a chatId.
            // Never fall back to activeSessionId — that causes cross-session pollution.
            const noticeText = msg.text || msg.content || msg.title || '';
            if (msg.chatId && noticeText) {
              // Verify the chatId actually maps to a session before adding
              const store = useStore.getState();
              if (store.sessions.has(msg.chatId)) {
                addMessage(msg.chatId, {
                  id: `notice-${Date.now()}`,
                  type: 'system',
                  text: noticeText,
                  timestamp: Date.now(),
                });
              }
            }
            break;
          }

          case 'group_created': {
            addGroup(msg.group);
            // Auto-create session for the new group
            const store2 = useStore.getState();
            const hasSession2 = Array.from(store2.sessions.values()).some(s => s.groupId === msg.group.id);
            if (!hasSession2) {
              const sessId = createGroupSession(msg.group);
              // Subscribe to the new group's channels
              ws.send(JSON.stringify({ type: 'subscribe_group', groupId: msg.group.id, chatId: sessId }));
            }
            break;
          }

          case 'group_updated':
            updateGroup(msg.group);
            break;

          case 'group_deleted':
            removeGroup(msg.groupId);
            break;

          case 'groups_list': {
            setGroups(msg.groups);
            // Auto-create session for default workspace group if not exists
            const store = useStore.getState();
            for (const group of msg.groups) {
              const hasSession = Array.from(store.sessions.values()).some(s => s.groupId === group.id);
              if (!hasSession) {
                createGroupSession(group);
              }
              // Request group history so new/reconnecting users see past messages
              ws.send(JSON.stringify({ type: 'get_group_history', groupId: group.id }));
            }
            break;
          }

          case 'group_history': {
            // Load persisted group messages into the group session
            const store = useStore.getState();
            const groupSession = Array.from(store.sessions.values()).find(
              (s) => s.groupId === msg.groupId,
            );
            if (groupSession && groupSession.messages.length === 0 && msg.messages.length > 0) {
              const chatMessages = msg.messages.map((m, i) => ({
                id: `grp-hist-${msg.groupId}-${i}`,
                type: m.role === 'user' ? 'user' as const : 'assistant' as const,
                text: m.text,
                timestamp: m.timestamp,
                botName: m.platform || undefined,  // sender name stored in platform field
                state: m.role === 'assistant' ? {
                  status: 'complete' as const,
                  userPrompt: '',
                  responseText: m.text,
                  toolCalls: [] as ToolCall[],
                  costUsd: m.costUsd,
                  durationMs: m.durationMs,
                } : undefined,
              }));
              const sessions = new Map(store.sessions);
              sessions.set(groupSession.id, { ...groupSession, messages: chatMessages });
              useStore.setState({ sessions });
            }
            break;
          }

          case 'group_message': {
            // Human message in a group chat — find the session by groupId
            const store = useStore.getState();
            const myUserId = localStorage.getItem('novax:userId') || '';
            const groupSession = Array.from(store.sessions.values()).find(
              (s) => s.groupId === msg.groupId,
            );
            if (groupSession) {
              // Skip if sent by us — we already added it locally in InputBar
              if (msg.senderId && myUserId && msg.senderId === myUserId) break;
              // Skip if we already have this message (check both raw and user-prefixed IDs)
              const hasMsg = groupSession.messages.some(
                (m) => m.id === msg.messageId || m.id === `user-${msg.messageId}`,
              );
              if (hasMsg) break;
              addMessage(groupSession.id, {
                id: msg.messageId,
                type: 'user',
                text: msg.text,
                timestamp: msg.timestamp,
                botName: msg.senderName,
              });
            }
            break;
          }

          case 'sessions_list': {
            mergeServerSessions(msg.sessions);
            // For restored sessions with no local messages, fetch history
            const curSessions = useStore.getState().sessions;
            for (const ss of msg.sessions) {
              const local = curSessions.get(ss.chatId);
              if (local && local.messages.length === 0) {
                ws.send(JSON.stringify({ type: 'get_session_history', sessionId: ss.id }));
                serverSessionIdMap.set(ss.id, ss.chatId);
              }
            }
            break;
          }

          case 'session_history': {
            const histChatId = serverSessionIdMap.get(msg.sessionId) || msg.sessionId;
            loadServerHistory(histChatId, msg.messages);
            serverSessionIdMap.delete(msg.sessionId);
            break;
          }

          case 'session_renamed':
            renameSession(msg.chatId, msg.title);
            break;

          case 'session_deleted':
            deleteSession(msg.chatId);
            break;

          case 'voice_call':
            setIncomingVoiceCall({
              sessionId: msg.sessionId,
              roomId: msg.roomId,
              token: msg.token,
              appId: msg.appId,
              userId: msg.userId,
              aiUserId: msg.aiUserId,
              chatId: msg.chatId,
              botName: msg.botName,
              prompt: msg.prompt,
            });
            break;

          case 'activity_event':
            addActivityEvent(msg.event);
            break;

          // ── Streaming ASR events ──
          case 'asr_started':
            setAsrState('active');
            break;

          case 'asr_transcript':
            setAsrPartialText(msg.text);
            break;

          case 'asr_error':
            setAsrState('error');
            console.error('Streaming ASR error:', msg.error);
            break;

          case 'asr_stopped':
            setAsrState('idle');
            break;

          case 'dm': {
            // Incoming direct message from another user
            const dm = msg.message;
            if (!dm) break;
            const myUserId = localStorage.getItem('novax:userId') || '';
            // Skip echo of own messages (already added locally by InputBar)
            if (dm.sender_id === myUserId) break;
            // Figure out who the other person is
            const otherUserId = dm.sender_id;
            const dmKey = `dm:${otherUserId}`;
            // Find or create the DM session
            const store = useStore.getState();
            let dmSessionId: string | null = null;
            for (const [sid, sess] of store.sessions) {
              if (sess.botName === dmKey) { dmSessionId = sid; break; }
            }
            if (!dmSessionId) {
              // Auto-create a DM session without switching active view
              const senderName = dm.sender_name || dm.sender_email?.split('@')[0] || otherUserId;
              dmSessionId = `dm-sess-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
              const newSessions = new Map(store.sessions);
              newSessions.set(dmSessionId, {
                id: dmSessionId,
                botName: dmKey,
                title: senderName,
                messages: [],
                createdAt: Date.now(),
                updatedAt: Date.now(),
              });
              // Persist immediately so DM sessions survive page refresh
              try {
                const obj: Record<string, any> = {};
                newSessions.forEach((v, k) => { obj[k] = v; });
                const uid = localStorage.getItem('novax:userId') || '';
                const sessKey = uid ? `novax:sessions:${uid}` : 'novax:sessions';
                localStorage.setItem(sessKey, JSON.stringify(obj));
              } catch { /* storage full */ }
              useStore.setState({ sessions: newSessions });
            }
            // Avoid duplicate messages (re-read state in case session was just created)
            const sess = useStore.getState().sessions.get(dmSessionId);
            if (sess && !sess.messages.find((m) => m.id === `dm-${dm.id}`)) {
              addMessage(dmSessionId, {
                id: `dm-${dm.id}`,
                type: dm.sender_id === myUserId ? 'user' : 'assistant',
                text: dm.content,
                timestamp: dm.created_at * 1000,
              });
              // Mark as unread if user is not currently viewing this DM session
              const currentSessionId = useStore.getState().activeSessionId;
              if (currentSessionId !== dmSessionId) {
                useStore.getState().markDmUnread(dmSessionId);
              }
            }
            break;
          }

          case 'pong':
            // heartbeat ack — nothing to do
            break;
        }
      } catch {
        // malformed message
      }
    };

    ws.onerror = () => {
      // Will trigger onclose
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setConnected(false);
      markRunningMessagesDisconnected();
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);

      // Reconnect with exponential backoff
      const delay = Math.min(
        RECONNECT_BASE * Math.pow(2, reconnectAttempt.current),
        RECONNECT_MAX,
      );
      reconnectAttempt.current++;
      reconnectTimer.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };
  }, [token, cleanup, setConnected, setBots, updateMessageState, addMessage, addMessageAttachment, markRunningMessagesDisconnected, addGroup, removeGroup, updateGroup, setGroups, createGroupSession, setIncomingVoiceCall, addActivityEvent, renameSession, deleteSession, mergeServerSessions, loadServerHistory, setAsrState, setAsrPartialText]);

  const send = useCallback(
    (msg: WSOutgoingMessage) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg));
      }
    },
    [],
  );

  /** Send raw binary data (e.g. PCM audio for streaming ASR) */
  const sendBinary = useCallback(
    (data: ArrayBuffer | Uint8Array) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(data);
      }
    },
    [],
  );

  const reconnect = useCallback(() => {
    reconnectAttempt.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    if (token) connect();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [token, connect, cleanup]);

  return { send, sendBinary, reconnect };
}
